import { Box, Button, Chip, Divider, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Observable } from 'rxjs';

import { RequireRole } from 'app/components/require-role';
import { SingleEditText } from 'app/components/single-edit-text';
import { Translate } from 'app/components/translate';
import { Role } from 'app/constants/role';
import { SeamStatus } from 'app/constants/seam-status';
import { TupleStatus } from 'app/enum/tuple-status.enum';
import { makeSeamPattern, SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { messagingService } from 'app/services/messaging.service';
import { seamPatternService } from 'app/services/seam-pattern.service';
import { seamRequestService } from 'app/services/seam-request.service';
import { seamTupleService } from 'app/services/seam-tuple.service';
import { ModelWithoutId } from 'app/type/model-with-id';
import { PreProdPattern } from 'app/type/pre-prod-pattern';
import { CsvBuilder } from 'app/utils/csv-builder';
import { LocalDownloader } from 'app/utils/local-downloader';
import { Strings } from 'app/utils/strings';

import { DeployedPatterns } from './deployed-patterns';
import { PatternAccordion } from './pattern-accordion';
import { deletePattern, saveSeamRequest, selectPattern, selectRequest } from '../slice';

interface PatternsProps extends RouteComponentProps {
    request: SeamRequest;
    patterns?: SeamPattern[];

    // redux actions
    selectRequest: (request?: SeamRequest) => void;
    saveSeamRequest: (request: SeamRequest) => Observable<SeamRequest>;
    selectPattern: (request?: ModelWithoutId<SeamPattern>) => void;
    deletePattern: (pattern: SeamPattern, request: SeamRequest) => Observable<void>;
}

class Patterns extends React.Component<PatternsProps> {
    public constructor(props: PatternsProps | Readonly<PatternsProps>) {
        super(props);

        this.startEdit = this.startEdit.bind(this);
    }

    public startEdit(formData: ModelWithoutId<SeamPattern>): void {
        this.props.selectPattern({ ...formData, viewOnly: !this.isEditable() });
        this.props.history.push(`/seam/request/${this.props.request.id}/patterns/${formData.id ?? 'new'}`);
    }
    public recomputePatterns(request: SeamRequest): void {
        seamPatternService.recomputePatterns(request).subscribe(() => {
            messagingService.showInfo('Re-compute has been scheduled, reload in a bit...');
        });
    }

    public render(): React.ReactNode {
        const request = this.props.request;
        const requestTypeStr = Strings.seamRequestType(request);
        const isReady = TupleStatus.READY === request.tupleStatus;

        return (
            <React.Fragment>
                <Box display='flex' alignItems='center'>
                    <Typography variant='h4'>
                        <Translate tkey={requestTypeStr} options={{ ns: 'headings' }} /> Patterns
                    </Typography>
                    <Box>
                        <Tooltip title='Download Patterns'>
                            <IconButton color='primary' size='small' onClick={() => this.exportPatterns()}>
                                <Icon>file_download</Icon>
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box flexGrow={1} />
                    <RequireRole roles={Role.TUNER}>
                        <Box>
                            <Button
                                variant='contained'
                                color='primary'
                                className='ml-2'
                                onClick={() => this.startEdit({ ...makeSeamPattern(), id: undefined })}
                            >
                                Create pattern
                            </Button>
                            <Button
                                variant='contained'
                                color='secondary'
                                className='ml-2'
                                onClick={() => this.recomputePatterns(request)}
                            >
                                Recompute pattern
                            </Button>
                            <Button variant='text' color='primary' onClick={() => alert('Not yet implemented')}>
                                Assign Labelers
                            </Button>
                        </Box>
                    </RequireRole>
                </Box>
                <Box>
                    <Typography>
                        Upon completion of the request patterns get deployed to SEAM module to facilitate matching. You
                        can click on the pattern to see more details and update it (if request status permits). The list
                        of patterns below may contain additional information depending on the state of the request:
                    </Typography>
                    <ul>
                        <li>
                            <strong>Tuples</strong>: Represents the number of MID tuples that matched the pattern at the
                            time of creating. This value will not change unless manually recomputed. When recomputing,
                            the value could change from day to day as the matching is always evolving.
                        </li>
                        <li>
                            <strong>Supersedes</strong>: If this value is showing it means that, upon deployment, this
                            pattern will deactivate the deployed pattern with id matching this value .
                        </li>
                        <li>
                            <strong>Deploy Id</strong>: This value appears after the request is deployed to pre-prod at
                            least once. It indicates the id of deployed pattern record associated with this pattern. If
                            the request was rejected by QA, modifying the pattern would update the linked deployed
                            pattern, instead of creating a new one.
                        </li>
                    </ul>
                </Box>
                <Divider />
                <Box>
                    {this.getMappedPattenrs()}
                    <Typography variant='h4' className='my-2'>
                        Deployed Patterns
                    </Typography>
                    <DeployedPatterns request={request} onEdit={(pattern) => this.editExistingPattern(pattern)} />
                    <SingleEditText
                        header='Notes'
                        value={request.notes}
                        emptyText='[No Notes]'
                        rows='4'
                        rowsMax='10'
                        className='my-2'
                        onSave={(notes) => {
                            this.props.saveSeamRequest({ ...request, notes }).subscribe((value) => {
                                this.props.selectRequest(value);
                            });
                        }}
                    />
                    <Typography variant='h5' className='my-2'>
                        All Matched Tuples Snapshot{this.getSyncChip()}
                    </Typography>
                    <Box display='flex' gap={2} mt={2}>
                        {this.isEditable() && (
                            <Button variant='contained' color='primary' onClick={this.handleRegenerateClick}>
                                Regenerate
                            </Button>
                        )}
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={this.handleDownloadClick}
                            disabled={!isReady}
                        >
                            Download
                        </Button>
                    </Box>
                </Box>
            </React.Fragment>
        );
    }

    private getMappedPattenrs() {
        if (undefined === this.props.patterns) {
            return <Typography variant='subtitle1'>Loading...</Typography>;
        }

        if (0 === this.props.patterns.length) {
            return (
                <Typography variant='subtitle1'>
                    No patterns defined. Click <strong>Create Pattern</strong> to start
                </Typography>
            );
        }

        return this.props.patterns.map((pattern, seq) => (
            <PatternAccordion
                key={pattern.id}
                pattern={pattern}
                sequence={seq + 1}
                onEdit={this.startEdit}
                editable={this.isEditable()}
                onDelete={() => {
                    // todo better confirmation
                    if (window.confirm('Are you sure you want to delete this pattern?')) {
                        this.props.deletePattern(pattern, this.props.request).subscribe();
                    }
                }}
            />
        ));
    }

    private editExistingPattern(pattern: PreProdPattern): void {
        this.startEdit({ ...makeSeamPattern(pattern), id: undefined });
    }

    private isEditable(): boolean {
        switch (this.props.request.currentPlace) {
            case SeamStatus.LABEL:
            case SeamStatus.TUNE:
            case SeamStatus.REVIEW:
                return true;
            default:
                return false;
        }
    }

    private getSyncChip() {
        if (TupleStatus.FAILED === this.props.request.tupleStatus) {
            return <Chip icon={<Icon>error</Icon>} label='Error' variant='outlined' color='error' />;
        }
        if (TupleStatus.READY !== this.props.request.tupleStatus) {
            return <Chip icon={<Icon>sync</Icon>} label='Out of Sync' variant='outlined' color='secondary' />;
        }

        return <Chip icon={<Icon>done</Icon>} label='Sync' variant='outlined' color='primary' />;
    }

    private handleRegenerateClick = (): void => {
        seamTupleService.exportTuples(this.props.request).subscribe(() => {
            seamRequestService.get(this.props.request.id).subscribe((updatedRequest: SeamRequest) => {
                this.props.selectRequest(updatedRequest);
                messagingService.showSuccess('Tuples Regenerated');
            });
        });
    };

    private handleDownloadClick = (): void => {
        seamTupleService.downloadTuples(this.props.request).subscribe((response: { url: string }) => {
            window.location.href = response.url;
            messagingService.showSuccess('Tuples Downloaded');
        });
    };

    /**
     * Since we already have all the data loaded locally already, build the CSV directly on frontend and simulate a
     * download.
     *
     * @private
     */
    private exportPatterns() {
        const builder = new CsvBuilder();
        // add headings
        builder.addRow([
            'Order',
            'Type',
            'Supersedes',
            'Operator',
            'MID',
            'Descriptor',
            'MCC',
            'City',
            'State',
            'Zip',
            'Country',
            'Channel',
            'Store Extract Field',
            'Store Extract Pattern',
        ]);

        this.props.patterns?.forEach((pattern, index) => {
            builder.addRow([
                `${index + 1}`,
                pattern.type,
                `${pattern.supersedes ?? ''}`,
                pattern.data.matchOperator,
                pattern.data.midPattern,
                pattern.data.descPattern,
                pattern.data.mccPattern.join(','),
                pattern.data.cityPattern,
                pattern.data.statePattern,
                pattern.data.zipPattern,
                pattern.data.countryPattern,
                pattern.data.subChannel,
                pattern.data.extractField,
                pattern.data.extractPattern,
            ]);
        });
        LocalDownloader.downloadBlob(builder.toBlob(), 'patterns.csv');
    }
}

const connected = connect(undefined, {
    selectRequest: selectRequest as ActionCreatorWithPayload<SeamRequest | undefined>,
    saveSeamRequest,
    deletePattern,
    selectPattern: selectPattern as ActionCreatorWithPayload<ModelWithoutId<SeamPattern> | undefined>,
})(withRouter(Patterns));

export { connected as Patterns };
/* eslint max-lines: off */
